import type { Point } from "../engines/coreComponents/Line";

export enum LinePathAction {
  LINE = "L",
  MOVE = "M",
  BEZIER = "B",
  ARC_TO = "A"
}

type MoveAction = {
  [LinePathAction.MOVE]: {
    x: number;
    y: number;
  };
};

type LineAction = {
  [LinePathAction.LINE]: {
    x: number;
    y: number;
  };
};

type BezierAction = {
  [LinePathAction.BEZIER]: {
    controlX1: number;
    controlY1: number;
    controlX2: number;
    controlY2: number;
    x: number;
    y: number;
  };
};

type ArcToAction = {
  [LinePathAction.ARC_TO]: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    radius: number;
  };
};

type PathSegment = MoveAction | LineAction | BezierAction | ArcToAction;

const lineVector = ({ start, end }: { start: Point; end: Point }) => {
  const x = end.x - start.x > 0 ? 1 : end.x - start.x === 0 ? 0 : -1;
  const y = end.y - start.y > 0 ? 1 : end.y - start.y === 0 ? 0 : -1;
  return { x, y };
};

const DEFAULT_RADIUS = 8;
type CalculatePathLineProps = {
  sourceX: number;
  targetX: number;
  sourceY: number;
  targetY: number;
  intermediatePoints: Point[];
  turningRadius?: number;
};

/**
 *
 * @param sourceX
 * @param targetX
 * @param sourceY
 * @param targetY
 * @param verticalAxisXPosition
 *
 * @returns A path object that contains the source and target coordinates for each line segment.
 *  - `{ [ key: LinePathAction ] : { source: number; target: number } }[]`
 */
export const calculatePathLine = ({
  sourceX,
  targetX,
  sourceY,
  targetY,
  intermediatePoints,
  turningRadius = DEFAULT_RADIUS
}: CalculatePathLineProps): {
  path: PathSegment[];
  direction: 1 | -1;
} => {
  const path: PathSegment[] = [];

  // set the starting point from where line will start.
  path.push({
    [LinePathAction.MOVE]: {
      x: sourceX,
      y: sourceY
    }
  });
  path.push({
    [LinePathAction.LINE]: {
      x: sourceX,
      y: sourceY
    }
  });

  let lastPoint = { x: sourceX, y: sourceY };
  if (intermediatePoints.length > 0) {
    const lastIntermediatePointIndex = intermediatePoints.length - 1;
    intermediatePoints.forEach((point, index) => {
      const vector = lineVector({ start: lastPoint, end: point });

      path.push({
        [LinePathAction.LINE]: {
          x: point.x - vector.x * turningRadius,
          y: point.y - vector.y * turningRadius
        }
      });

      const nextPoint =
        lastIntermediatePointIndex !== index
          ? intermediatePoints[index + 1]
          : {
              x: targetX,
              y: targetY
            };

      const nextPointVector = lineVector({ start: point, end: nextPoint });
      path.push({
        [LinePathAction.ARC_TO]: {
          x1: point.x,
          y1: point.y,
          x2: point.x + nextPointVector.x * turningRadius,
          y2: point.y + nextPointVector.y * turningRadius,
          radius: turningRadius
        }
      });
      lastPoint = point;
    });
  }

  path.push({
    [LinePathAction.LINE]: {
      x: targetX,
      y: targetY
    }
  });

  return { path, direction: targetX >= lastPoint.x ? 1 : -1 };
};
